import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "../wrappers/Box";
import Button from "../wrappers/Button";
import Checkbox from "../wrappers/Checkbox";
import Dialog from "../wrappers/Dialog";
import DialogActions from "../wrappers/DialogActions";
import DialogContent from "../wrappers/DialogContent";
import DialogTitle from "../wrappers/DialogTitle";
import FormControlLabel from "../wrappers/FormControlLabel";
import Typography from "../wrappers/Typography";
import { includesValue, isSameValue } from "./SearchFilters.helpers";

/**
 * Renderiza um diálogo que confirma a seleção completa de opções.
 *
 * @param {object} pProps Propriedades do componente.
 * @param {string} pProps.id Identificador estável do diálogo.
 * @param {string} pProps.title Título associado ao diálogo.
 * @param {boolean} pProps.open Controla a abertura do diálogo.
 * @param {Array<*>} pProps.value Valores confirmados pelo consumidor.
 * @param {Array<object>} pProps.options Opções completas da lista.
 * @param {Function} pProps.onClose Descarta o rascunho e fecha o diálogo.
 * @param {Function} pProps.onConfirm Recebe a lista confirmada na ordem das opções.
 * @returns {React.ReactElement}
 */
const SearchFilterOptionsDialog = (pProps) => {
  const { id, title, open, value, options, onClose, onConfirm } = pProps;
  const [draftValue, setDraftValue] = useState(() => [...value]);
  const xFirstEnabledOptionIndex = options.findIndex((pOption) => !pOption.disabled);
  const xTitleId = `${id}-title`;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setDraftValue([...value]);

    return undefined;
  }, [open]);

  /** Descarta o rascunho para qualquer motivo de fechamento não confirmado. */
  const pvHandleClose = () => {
    setDraftValue([...value]);
    onClose();
  };

  /** Alterna somente o rascunho local da opção habilitada. */
  const pvToggleOption = (pOption) => {
    if (pOption.disabled) {
      return;
    }

    setDraftValue((pCurrentValue) =>
      includesValue(pCurrentValue, pOption.value)
        ? pCurrentValue.filter((pValue) => !isSameValue(pValue, pOption.value))
        : [...pCurrentValue, pOption.value],
    );
  };

  /** Confirma uma única lista ordenada como as opções recebidas. */
  const pvHandleConfirm = () => {
    const xConfirmedValue = options
      .filter((pOption) => includesValue(draftValue, pOption.value))
      .map((pOption) => pOption.value);

    onConfirm(xConfirmedValue);
  };

  return (
    <Dialog aria-labelledby={xTitleId} disableAutoFocus onClose={pvHandleClose} open={open}>
      <DialogTitle id={xTitleId} variant="button" sx={{ pb: 1, pt: 2 }}>
        <b>{title}</b>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: 0, overflowY: "auto" }}>
        <Box role="group" aria-label={title} sx={{ display: "flex", flexDirection: "column" }}>
          {options.map((pOption, pIndex) => (
            <FormControlLabel
              key={pOption.id}
              control={
                <Checkbox
                  autoFocus={pIndex === xFirstEnabledOptionIndex}
                  checked={includesValue(draftValue, pOption.value)}
                  onChange={() => pvToggleOption(pOption)}
                  size="small"
                />
              }
              disabled={pOption.disabled}
              label={<Typography variant="caption">{pOption.label}</Typography>}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button onClick={pvHandleClose} type="button" variant="text" size="small">
          Cancelar
        </Button>
        <Button onClick={pvHandleConfirm} type="button" variant="contained" size="small">
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SearchFilterOptionsDialog.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.arrayOf(PropTypes.any).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      disabled: PropTypes.bool,
      neutral: PropTypes.bool,
    }),
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default SearchFilterOptionsDialog;
